import React, { forwardRef, PropsWithChildren } from 'react'
import block from 'bem-cn'

import { Loader } from 'components/UIKit/Loader'

import './ControllerWrapper.scss'

export enum ControllerWrapperVariant {
  DEFAULT = 'default',
  OUTLINED = 'outlined',
}

export enum ControllerWrapperSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export type ControllerWrapperSharedProps = {
  caption?: React.ReactNode
  errorText?: React.ReactNode
  description?: React.ReactNode

  suffix?: React.ReactNode
  prefix?: React.ReactNode

  onClick?: (e: React.MouseEvent<HTMLLabelElement>) => void

  variant?: ControllerWrapperVariant
  size?: ControllerWrapperSize

  isError?: boolean
  isLoading?: boolean
  disabled?: boolean
  required?: boolean
}

export type ControllerWrapperProps = ControllerWrapperSharedProps &
  PropsWithChildren<{
    className?: string
    isFocused?: boolean
  }>

const b = block('controller-wrapper')

export const ControllerWrapper = forwardRef<HTMLLabelElement, ControllerWrapperProps>(
  (
    {
      className,
      caption,
      isError,
      errorText,
      description,
      children,
      suffix,
      isFocused,
      disabled,
      required,
      isLoading,
      variant = ControllerWrapperVariant.DEFAULT,
      size = ControllerWrapperSize.MEDIUM,
      prefix,
      onClick,
    },
    ref,
  ) => {
    const showBottom = (isError && errorText) || description
    const hasSuffix = Boolean(suffix)
    const hasPrefix = Boolean(prefix || isLoading)

    return (
      <label ref={ref} onClick={onClick} className={b({ required }).mix(className)}>
        {caption && <span className={b('caption', { required })}>{caption}</span>}

        <div className={b('field', { hasPrefix, hasSuffix, isFocused, variant, size, disabled })}>
          <div className={b('field-content')}>{children}</div>

          {prefix && <div className={b('prefix')}>{prefix}</div>}

          {(suffix || isLoading) && (
            <div className={b('suffix')}>
              {isLoading && <Loader />}
              {suffix}
            </div>
          )}
        </div>

        {showBottom && (
          <div className={b('bottom')}>
            {description && <span className={b('description')}>{description}</span>}
            {isError && errorText && <span className={b('error')}>{errorText}</span>}
          </div>
        )}
      </label>
    )
  },
)

ControllerWrapper.displayName = 'ControllerWrapper'
